import { ApiError } from '@fc/core'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Spinner,
} from '@fc/ui'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2 } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BrandLockup } from '../../components/brand'
import { ThemeToggle } from '../../components/theme-toggle'
import { acceptInvitation, previewInvitation } from '../../lib/members-api'
import { SCOPE_LABEL } from './shared'

const MIN_PASSWORD = 10

function AcceptForm({ token, email, orgName }: { token: string; email: string; orgName: string }) {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [done, setDone] = useState(false)

  const mutation = useMutation({
    mutationFn: () => acceptInvitation({ token, name, password }),
    onSuccess: () => setDone(true),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    mutation.mutate()
  }

  if (done) {
    return (
      <div className="space-y-4 py-6 text-center">
        <CheckCircle2 className="mx-auto size-10 text-success" aria-hidden="true" />
        <div className="space-y-1">
          <h2 className="font-display text-xl font-semibold">You’re all set!</h2>
          <p className="text-sm text-muted">
            Your account for {orgName} is ready. Sign in to get started.
          </p>
        </div>
        <Button onClick={() => navigate('/login')} className="w-full">
          Go to sign in
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" value={email} readOnly disabled />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="name">Your name</Label>
        <Input
          id="name"
          autoComplete="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jane Doe"
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="password">Create a password</Label>
        <Input
          id="password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••"
          minLength={MIN_PASSWORD}
          required
        />
        <p className="text-xs text-dim">At least {MIN_PASSWORD} characters.</p>
      </div>

      {mutation.isError && (
        <p className="text-sm text-danger" role="alert">
          {mutation.error instanceof ApiError
            ? mutation.error.message
            : 'Could not accept the invitation.'}
        </p>
      )}

      <Button
        type="submit"
        className="w-full"
        disabled={mutation.isPending || name.trim() === '' || password.length < MIN_PASSWORD}
      >
        {mutation.isPending ? <Spinner /> : null}
        Accept invitation
      </Button>
    </form>
  )
}

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['invite-preview', token],
    queryFn: () => previewInvitation(token!),
    enabled: !!token,
    retry: false,
  })

  const invalid = isError || !data || data.status !== 'pending' || data.expired

  return (
    <div className="grid min-h-dvh place-items-center bg-bg px-6 py-12">
      <div
        className="absolute inset-x-0 top-0 -z-10 h-80 bg-brand-grad-soft blur-3xl"
        aria-hidden
      />
      <div className="w-full max-w-sm">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/" aria-label="FC ChMS home">
            <BrandLockup />
          </Link>
          <ThemeToggle />
        </div>

        <Card>
          {isLoading ? (
            <CardContent>
              <div className="flex items-center justify-center gap-2 py-10 text-muted">
                <Spinner /> Checking your invitation…
              </div>
            </CardContent>
          ) : invalid ? (
            <>
              <CardHeader>
                <CardTitle>Invitation unavailable</CardTitle>
                <CardDescription>
                  This invitation link is invalid, has already been used, was revoked, or has
                  expired.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">Go to sign in</Link>
                </Button>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Join {data.orgName}</CardTitle>
                <CardDescription>
                  You’ve been invited
                  {data.roleName ? (
                    <>
                      {' '}
                      as <span className="font-medium text-fg">{data.roleName}</span>
                    </>
                  ) : null}
                  {data.scopeType !== 'org' ? ` (${SCOPE_LABEL[data.scopeType]})` : ''}. Set up your
                  account to continue.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AcceptForm token={token!} email={data.email} orgName={data.orgName} />
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
